import { getCurrencySymbol } from '@gameday/common/functions/currency'
import shortid from 'shortid'
import styled, { css } from 'styled-components'

import theme, { rem } from '../../utils/theme'

const StyledPaymentSummary = styled.div`
  width: 100%;
  padding: ${rem(10)} 0 ${rem(12)};
  margin: 0 0 ${rem(16)};
  border-top: ${theme.borders.default};
  border-bottom: ${theme.borders.default};
`

const StyledTable = styled.table`
  width: 100%;
  text-align: right;
`

const StyledBody = styled.tbody`
  width: 100%;
`

const StyledRow = styled.tr`
  > th,
  > td {
    font-size: ${theme.typography.fontSizeTinyish};
    padding: ${rem(2)} 0;

    ${props =>
      props.isTotal &&
      css`
        font-size: ${theme.typography.fontSizeSm};
        padding: ${rem(14)} 0 0;
      `}

    ${props =>
      props.isInstalment &&
      css`
        border-bottom: ${theme.borders.default};
      `}
  }

  > th {
    font-weight: ${theme.typography.fontWeightNormal};
    color: ${`var(--gameday-body-textColorLight, ${theme.body.textColorLight})`};

    ${props =>
      props.isTotal &&
      css`
        font-weight: ${theme.typography.fontWeightBolder};
      `}
  }

  > td {
    width: ${rem(108)};
    font-weight: ${theme.typography.fontWeightBolder};
    color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};

    ${props =>
      props.isDiscount &&
      css`
        color: ${`var(--gameday-body-textColorLight, ${theme.body.textColorLight})`};
      `}

    > span {
      color: ${theme.body.textColorExtraLight};
    }
  }
`

const PaymentSummary = ({ data, displayPriceAsZero = false }) => {
  const { currencyCode, subTotal, discounts, promoCodes, fees, voucher, total, instalment, insurance, tax } = data
  const { useInstalment } = instalment || {}

  let displayTotal = total.rawValue

  if (insurance && insurance.rawValue > 0) {
    displayTotal = displayTotal + insurance.rawValue
  }

  return (
    <StyledPaymentSummary>
      <StyledTable>
        <StyledBody>
          <StyledRow>
            <th>{subTotal.name}</th>
            <td>
              {Number(subTotal.rawValue) === 0
                ? displayPriceAsZero
                  ? `${getCurrencySymbol(currencyCode)} 0.00`
                  : 'FREE'
                : `${getCurrencySymbol(currencyCode)} ${subTotal.rawValue.toFixed(2)}`}
              {Number(subTotal.rawValue) === 0 && !displayPriceAsZero ? null : <span> {currencyCode}</span>}
            </td>
          </StyledRow>
          {/* biome-ignore lint/complexity/useOptionalChain: TODO */}
          {discounts &&
            discounts.map(discount => (
              <StyledRow key={shortid()} isDiscount>
                <th>{discount.name}</th>
                <td>
                  {`- ${getCurrencySymbol(currencyCode)} ${discount.rawValue.toFixed(2)} `}
                  <span> {currencyCode}</span>
                </td>
              </StyledRow>
            ))}
          {/* biome-ignore lint/complexity/useOptionalChain: TODO */}
          {promoCodes &&
            promoCodes.map(promoCode => (
              <StyledRow key={shortid()} isDiscount>
                <th>{promoCode.name}</th>
                <td>
                  {`- ${getCurrencySymbol(currencyCode)} ${promoCode.rawValue.toFixed(2)} `}
                  <span> {currencyCode}</span>
                </td>
              </StyledRow>
            ))}
          {voucher && voucher.rawValue !== 0 && (
            <StyledRow isDiscount>
              <th>{voucher.name}</th>
              <td>
                {`- ${getCurrencySymbol(currencyCode)} ${voucher.rawValue.toFixed(2)} `}
                <span> {currencyCode}</span>
              </td>
            </StyledRow>
          )}
          {fees.rawValue !== 0 && (
            <StyledRow>
              <th>{fees.name}</th>
              <td>
                {`${getCurrencySymbol(currencyCode)} ${fees.rawValue} `}
                <span> {currencyCode}</span>
              </td>
            </StyledRow>
          )}
          {insurance && (
            <StyledRow>
              <th>{insurance.name}</th>
              <td>
                {`${getCurrencySymbol(currencyCode)} ${insurance.rawValue.toFixed(2)} `}
                <span> {currencyCode}</span>
              </td>
            </StyledRow>
          )}
          <StyledRow isTotal>
            <th>{total.name}</th>
            <td>
              {Number(displayTotal) === 0 && !voucher
                ? displayPriceAsZero
                  ? `${getCurrencySymbol(currencyCode)} 0.00`
                  : 'FREE'
                : `${getCurrencySymbol(currencyCode)} ${displayTotal.toFixed(2)}`}
              {Number(displayTotal) === 0 && !voucher && !displayPriceAsZero ? null : <span> {currencyCode}</span>}
            </td>
          </StyledRow>
          {tax && tax.rawValue > 0 && (
            <StyledRow>
              <th style={{ fontStyle: 'italic', opacity: 0.7 }}>{tax.name}</th>
              <td style={{ fontStyle: 'italic', opacity: 0.7 }}>
                {`${getCurrencySymbol(currencyCode)} ${tax.rawValue.toFixed(2)} `}
                <span> {currencyCode}</span>
              </td>
            </StyledRow>
          )}
          {useInstalment && (
            <StyledRow isInstalment>
              <td colSpan={'2'} />
            </StyledRow>
          )}

          {useInstalment && (
            <StyledRow isTotal>
              <th>Due Today</th>
              <td>
                {Number(instalment.rawValue) === 0 && !voucher
                  ? displayPriceAsZero
                    ? `${getCurrencySymbol(currencyCode)} 0.00`
                    : 'FREE'
                  : `${getCurrencySymbol(currencyCode)} ${instalment.rawValue.toFixed(2)}`}
                {Number(instalment.rawValue) === 0 && !voucher && !displayPriceAsZero ? null : (
                  <span> {currencyCode}</span>
                )}
              </td>
            </StyledRow>
          )}
        </StyledBody>
      </StyledTable>
    </StyledPaymentSummary>
  )
}

PaymentSummary.defaultProps = {
  data: {
    currencyCode: '',
    discounts: [],
    promoCodes: [],
    subTotal: {},
    fees: {},
    total: {},
  },
}

export default PaymentSummary
